import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import FormEmbed from "@/components/FormEmbed";

const QuoteFormContext = createContext<{ openQuoteForm: () => void }>({
  openQuoteForm: () => {},
});

export const useQuoteForm = () => useContext(QuoteFormContext);

export const QuoteFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openQuoteForm = useCallback(() => setOpen(true), []);
  const closeQuoteForm = useCallback(() => setOpen(false), []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <QuoteFormContext.Provider value={{ openQuoteForm }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/65 backdrop-blur-sm p-4 overflow-y-auto"
            onClick={(e) => { if (e.target === e.currentTarget) closeQuoteForm(); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="relative w-full max-w-lg my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button lives on the outer wrapper (no overflow clip) so it's always fully visible */}
              <button
                onClick={closeQuoteForm}
                className="absolute -top-3 -right-3 z-30 h-9 w-9 rounded-full bg-secondary text-secondary-foreground shadow-lg hover:scale-105 transition-transform flex items-center justify-center"
                aria-label="Close form"
              >
                <X className="h-5 w-5" />
              </button>

              {/* Capped-height shell: header stays put, the form scrolls inside so it always fits on screen */}
              <div className="widget-shell flex flex-col max-h-[calc(100dvh-2rem)]">
                <div className="bg-card px-6 pt-6 pb-2 shrink-0">
                  <h3 className="font-heading text-2xl text-foreground font-bold tracking-tight">
                    Get Your Free Estimate
                  </h3>
                  <p className="text-muted-foreground text-sm mt-1">
                    Tell us about your space — we'll reply within hours.
                  </p>
                </div>

                {/* Embedded LeadConnector form — scrolls within the modal on shorter screens */}
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
                  <FormEmbed id="popup-l0Xup9Zza7GkOhde8oby" className="p-4 sm:p-6" />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </QuoteFormContext.Provider>
  );
};
